import {
  dedupe,
  GaslessSongMintDto,
  GaslessSoundMintDto,
  SongCompositionDTO,
  SongDto,
  SoundDto,
  StemType
} from '@arpeggi/shared'
import { clone } from 'mobx-state-tree'
import { IHoneyPotFields } from '../components/core/HoneyPotFields'
import { UploadSoundModalForm } from '../components/studio/mint/upload-sound-modal/MintSoundUploadPage'
import { useAuth } from '../hooks/useAuth'
import { IClipStore } from '../stores/song/clip/ClipStore'
import { ISongStore } from '../stores/song/SongStore'
import { IAudioStemStore } from '../stores/song/stem/AudioStemStore'
import { IStemStore } from '../stores/song/StemStore'
import { IStudioStore } from '../stores/StudioStore'
import { objectToFormData } from '../utils/api'
import {
  getMetadataForSong,
  getMetadataForSound,
  getMetadataForStem,
  getSubcomponentsForStem
} from './MintUtils'

const useMintGasless = () => {
  const { authenticatedPost } = useAuth()
  /**
   * Package up the song, and all the stems, to POST to the API
   * The API will process the request and perform gasless minting
   */
  const mintSongGasless = async ({
    studio,
    song,
    songCompositionDto,
    genres,
    customArtistAddress,
    honeyPotFields
  }: {
    studio: IStudioStore
    song: ISongStore
    songCompositionDto: SongCompositionDTO
    genres?: string[]
    customArtistAddress?: string
    honeyPotFields: IHoneyPotFields
  }) => {
    let stemDtos: SoundDto[] = []

    const songSubcomponents = []

    for (let i = 0; i < song.stems.length; i++) {
      let stem = clone(song.stems[i])

      // We don't render + register stems that are MIDI remixes of a stem from another song.
      // But we do add the id as a subcomponent of the song.
      if (song.stems[i].isRemixed) {
        const remixedStemArpId = stem.remixedStemId
        // It is required to add this to stemDtos because the gasless minting process uses
        // this to check whether the stem is a remix.
        // However, it currently doesn't get rendered or minted.
        stemDtos.push({
          arpMediaId: remixedStemArpId,
          metadata: getMetadataForStem(studio, song.stems[i], song),
          subcomponents: [],
          dataUri: ''
        })
        songSubcomponents.push(remixedStemArpId)
        continue
      }

      // We also don't render + register audio stems with one clip, since they're not interesting, or are duplicates of the clip.
      // But we do should add the clips samples as subcomponents of the song.
      if (audioStemHasOneClip(song.stems[i])) {
        songSubcomponents.push(...getArpIdsFromAllClips(song.stems[i]))
        continue
      }

      stem.trimArrangementTo(song.songRealLength)
      const metadata = getMetadataForStem(studio, stem, song)
      const subcomponents = getSubcomponentsForStem(studio, stem)
      stemDtos.push({
        metadata,
        dataUri: '',
        arpeggiComposition: stem.serializeStemForStorage(),
        subcomponents
      })
    }

    const songDto: SongDto = {
      stemDtos: stemDtos,
      metadata: getMetadataForSong(song, genres),
      dataUri: '',
      subcomponents: dedupe(songSubcomponents),
      arpeggiComposition: song.serializeSongForStorage()
    }

    // // Uncomment this if you want to locally test what the offline rendering of the song will sound like
    // // you also have to remove @arpeggi/server-shared imports from renderStatusProcessor
    // // and make the url import in the offlineRenderService to be localhost
    // const wav = await renderWav(true, songCompositionDto.songLengthInSeconds, songCompositionDto)
    // console.log('WAV', wav)
    // const uri = wav.toDataURI()
    // const audio = new Audio(uri)
    // audio.play()
    // return

    const gaslessSongMintDto: GaslessSongMintDto = {
      songDto: songDto,
      jobId: '',
      walletAddress: customArtistAddress ? customArtistAddress : '', // this is added by middleware in the api
      songCompositionDto: songCompositionDto,
      sampleIds: song.currentSamples,
      ...honeyPotFields
    }

    return await (
      await authenticatedPost('/api/v2/mints/songs', gaslessSongMintDto)
    ).data.jobId
  }

  /**
   * POST the sound file and metadata to the API.
   * The API will process the request and perform gasless minting.
   */
  const mintSoundGasless = async ({
    file,
    uploadSoundData,
    customArtistAddress,
    customTags
  }: {
    file: File
    uploadSoundData: UploadSoundModalForm
    customArtistAddress?: string
    customTags?: string[]
  }) => {
    console.log('minting sound: ', uploadSoundData, 'size: ', file.size)

    const soundDto: SoundDto = {
      metadata: getMetadataForSound(uploadSoundData, customTags),
      subcomponents: [] // can use this in the future, when we allow minting "edited" samples
    }

    const gaslessSoundMintDto: GaslessSoundMintDto = {
      soundDto: soundDto,
      jobId: '',
      walletAddress: customArtistAddress || '' // this is added by middleware in the api
    }

    const formData = objectToFormData(gaslessSoundMintDto)
    formData.append('soundFile', file, file.name)

    const resp = await authenticatedPost('/api/v2/mints/sounds', formData)
    return resp.data.jobId
  }

  return { mintSongGasless, mintSoundGasless }
}

/**
 * Returns true if the stem is an AudioStems containing a single clip.
 */
const audioStemHasOneClip = (stem: IStemStore): boolean => {
  if (stem.stemType === StemType.AUDIO) {
    const audioStem = stem as IAudioStemStore
    return audioStem.clips.length === 1
  }
  return false
}

/** Returns the ids of all clips in this stem, if its an audio stem. */
const getArpIdsFromAllClips = (stem: IStemStore): number[] => {
  if (stem.stemType === StemType.AUDIO) {
    const audioStem = stem as IAudioStemStore
    return audioStem.clips.map((clip: IClipStore) => {
      return clip.sampleWithEffects.sampleIndex
    })
  }
  return []
}

export default useMintGasless
